import { Box, Button, Collapse, Group, Menu, MultiSelect, ScrollArea, Select, Stack, Switch, Text, TextInput, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconChevronDown, IconChevronUp, IconColumns, IconHistory, IconSearch, IconSettings } from '@tabler/icons-react';
import React, { useCallback, useState } from 'react';

export type ViewMode = 'compact' | 'standard' | 'detailed';

export interface ColumnMeta {
    field: string;
    title: string;
    essential?: boolean;
    defaultVisible?: boolean;
}

export interface FilterClause {
    field: string;
    operator: 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'regex';
    value: string;
}

export interface AdvancedSearchConfig {
    kinds: string[];
    viewMode: ViewMode;
    dateRange: [Date | null, Date | null];
    fullText?: string;
    property?: string;
    clauses?: FilterClause[];
    hydrateAll?: boolean;
    quickRangeDays?: number | null;
    live?: boolean;
    changedBy?: string[];
}

export interface WorkflowComponentsSearchProps {
    title?: string;
    allKinds: string[];
    selectedKinds: string[];
    onKindsChange: (kinds: string[]) => void;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
    propertyList: Array<{ value: string; label: string }>;
    selectedProperty: string;
    onPropertyChange: (prop: string) => void;
    fullText: string;
    onFullTextChange: (text: string) => void;
    dateRange: [Date | null, Date | null];
    onDateRangeChange: (range: [Date | null, Date | null]) => void;
    columnsByCategory?: Record<string, ColumnMeta[]>;
    onToggleColumn?: (fieldName: string) => void;
    valueSuggestions?: string[];
    actorSuggestions?: string[];
    onApply: (config: AdvancedSearchConfig) => void;
    onClear: () => void;
    collapsible?: boolean;
}

const KIND_OPTIONS = (kinds: string[]) => kinds.map((t: string) => ({ value: t, label: t }));

export const WorkflowComponentsSearch: React.FC<WorkflowComponentsSearchProps> = ({
    title = 'Workflow Components Search',
    allKinds,
    selectedKinds,
    onKindsChange,
    viewMode,
    onViewModeChange,
    propertyList,
    selectedProperty,
    onPropertyChange,
    fullText,
    onFullTextChange,
    dateRange,
    onDateRangeChange,
    columnsByCategory,
    onToggleColumn,
    onApply,
    onClear,
    valueSuggestions,
    actorSuggestions,
    collapsible = true
}) => {
    const [open, setOpen] = useState(true);
    const [localClauses, setLocalClauses] = useState<FilterClause[]>([]);
    const [hydrateAll, setHydrateAll] = useState<boolean>(false);
    const [quickRangeDays, setQuickRangeDays] = useState<number | null>(null);
    const [live, setLive] = useState<boolean>(false);
    const [changedBy, setChangedBy] = useState<string[]>([]);

    const canToggleColumns = !!columnsByCategory && !!onToggleColumn;

    const addClause = useCallback(() => {
        setLocalClauses((prev) => [...prev, { field: selectedProperty || 'name', operator: 'contains', value: '' }]);
    }, [selectedProperty]);

    const updateClause = useCallback((index: number, patch: Partial<FilterClause>) => {
        setLocalClauses((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    }, []);

    const removeClause = useCallback((index: number) => {
        setLocalClauses((prev) => prev.filter((_: any, i) => i !== index));
    }, []);

    const apply = useCallback(() => {
        onApply({
            kinds: selectedKinds.length ? selectedKinds : allKinds,
            viewMode,
            dateRange,
            fullText,
            property: selectedProperty,
            clauses: localClauses,
            hydrateAll,
            quickRangeDays,
            live,
            changedBy
        });
    }, [onApply, selectedKinds, allKinds, viewMode, dateRange, fullText, selectedProperty, localClauses, hydrateAll, quickRangeDays, live, changedBy]);

    const clear = useCallback(() => {
        setLocalClauses([]);
        setHydrateAll(false);
        onClear();
        setQuickRangeDays(null);
        setLive(false);
        setChangedBy([]);
    }, [onClear]);

    return (
        <Box>
            <Group justify="space-between" mb="sm">
                <Title order={4}>{title}</Title>
                {collapsible && (
                    <Button
                        variant="subtle"
                        size="sm"
                        leftSection={open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? 'Collapse' : 'Expand'}
                    </Button>
                )}
            </Group>
            <Collapse in={!collapsible || open}>
                <Group wrap="wrap" gap="sm" ta="end">
                    <Select
                        label="Component Type(s)"
                        data={KIND_OPTIONS(allKinds)}
                        value={undefined}
                        placeholder={selectedKinds.length ? selectedKinds.join(', ') : 'All'}
                        onChange={(v: string | null) => onKindsChange(v ? [v] : [])}
                        searchable
                        style={{ minWidth: 240 }}
                    />
                    <Select
                        label="View Mode"
                        data={[{ value: 'compact', label: 'Compact' }, { value: 'standard', label: 'Standard' }, { value: 'detailed', label: 'Detailed' }]}
                        value={viewMode}
                        onChange={(value) => onViewModeChange((value ?? 'standard') as ViewMode)}
                        w={140}
                    />
                    <Select
                        label="Search Property"
                        data={propertyList}
                        value={selectedProperty}
                        onChange={(v: string | null) => onPropertyChange(v ?? 'name')}
                        style={{ minWidth: 200 }}
                    />
                    {valueSuggestions && valueSuggestions.length > 0 ? (
                        <Select
                            label="Search Value / Suggestions"
                            searchable
                            data={valueSuggestions.map(v => ({ value: v, label: v }))}
                            value={fullText || null}
                            onChange={(v: string | null) => onFullTextChange(v || '')}
                            style={{ minWidth: 280 }}
                            placeholder="Pick or type a value"
                            allowDeselect
                        />
                    ) : (
                        <TextInput
                            label="Search Value / Full Text"
                            placeholder="Enter search term or leave empty"
                            value={fullText}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFullTextChange(e.currentTarget.value)}
                            style={{ minWidth: 280 }}
                        />
                    )}

                    <MultiSelect
                        label="Changed By (user/actor)"
                        placeholder="Add one or more users/actors"
                        data={(actorSuggestions || []).map(v => ({ value: v, label: v }))}
                        searchable
                        clearable
                        value={changedBy}
                        onChange={setChangedBy}
                        style={{ minWidth: 260 }}
                    />
                    <DatePickerInput
                        type="range"
                        label="Updated Date Range"
                        value={dateRange}
                        onChange={(v) => onDateRangeChange(v as [Date | null, Date | null])}
                    />

                    <Select
                        label="Quick Range"
                        placeholder="Select"
                        data={[
                            { value: '1', label: 'Last 24 hours' },
                            { value: '7', label: 'Last 7 days' },
                            { value: '30', label: 'Last 30 days' },
                            { value: '90', label: 'Last 90 days' },
                            { value: 'all', label: 'All' }
                        ]}
                        value={quickRangeDays === null ? null : String(quickRangeDays)}
                        onChange={(v: string | null) => {
                            if (!v || v === 'all') { setQuickRangeDays(null); return; }
                            const days = Number(v);
                            setQuickRangeDays(Number.isFinite(days) ? days : null);
                            if (Number.isFinite(days)) {
                                const to = new Date();
                                const from = new Date();
                                from.setDate(to.getDate() - days);
                                onDateRangeChange([from, to]);
                            }
                        }}
                        w={160}
                    />
                    {quickRangeDays !== null && (
                        <Group gap={4} ta="center">
                            <Text size="xs" c="dimmed">Range:</Text>
                            <Text size="xs">Last {quickRangeDays} day(s)</Text>
                            <Button size="compact-xs" variant="light" onClick={() => { setQuickRangeDays(null); onDateRangeChange([null, null]); }}>Clear</Button>
                        </Group>
                    )}

                    <Stack gap={2}>
                        <Text size="xs" c="dimmed">Live Updates</Text>
                        <Switch checked={live} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLive(e.currentTarget.checked)} />
                    </Stack>

                    {canToggleColumns && (
                        <Menu shadow="md" width={300} withinPortal>
                            <Menu.Target>
                                <Button leftSection={<IconColumns size={16} />} variant="light">
                                    Columns
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <ScrollArea h={400}>
                                    {Object.entries(columnsByCategory!).map(([categoryName, columns]) => (
                                        <Box key={categoryName}>
                                            <Menu.Label>{categoryName.charAt(0).toUpperCase() + categoryName.slice(1)} Fields</Menu.Label>
                                            {columns.map((col) => (
                                                <Menu.Item key={col.field} onClick={() => onToggleColumn!(col.field)}>
                                                    <Group justify="space-between" w="100%">
                                                        <Text size="sm">{col.title}</Text>
                                                        {col.essential && <Text size="xs" c="blue">Essential</Text>}
                                                    </Group>
                                                </Menu.Item>
                                            ))}
                                        </Box>
                                    ))}
                                    <Menu.Divider />
                                    <Menu.Item leftSection={<IconSettings size={16} />} onClick={() => onViewModeChange('standard')}>
                                        Reset to Defaults
                                    </Menu.Item>
                                </ScrollArea>
                            </Menu.Dropdown>
                        </Menu>
                    )}

                    <Menu withinPortal>
                        <Menu.Target>
                            <Button leftSection={<IconHistory size={16} />} variant="light">Advanced</Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Stack p="xs" gap="xs" w={360}>
                                <Text size="sm" c="dimmed">Add property clauses</Text>
                                {localClauses.map((c, i) => (
                                    <Group key={i} gap="xs" ta="end">
                                        <Select w={120} data={propertyList} value={c.field} onChange={(v: string | null) => updateClause(i, { field: v || 'name' })} />
                                        <Select w={120} data={[
                                            { value: 'contains', label: 'contains' },
                                            { value: 'equals', label: 'equals' },
                                            { value: 'startsWith', label: 'starts with' },
                                            { value: 'endsWith', label: 'ends with' },
                                            { value: 'regex', label: 'regex' }
                                        ]} value={c.operator} onChange={(v: string | null) => updateClause(i, { operator: (v as FilterClause['operator']) || 'contains' })} />
                                        <TextInput w={140} value={c.value} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateClause(i, { value: e.currentTarget.value })} />
                                        <Button variant="light" color="red" onClick={() => removeClause(i)}>Remove</Button>
                                    </Group>
                                ))}
                                <Group gap="xs">
                                    <Button variant="light" onClick={addClause}>Add Clause</Button>
                                    <Button variant={hydrateAll ? 'filled' : 'light'} onClick={() => setHydrateAll((v) => !v)}>Hydrate All Drafts</Button>
                                </Group>
                            </Stack>
                        </Menu.Dropdown>
                    </Menu>

                    <Button leftSection={<IconSearch size={16} />} onClick={apply}>Search</Button>
                    <Button variant="light" onClick={apply}>Refresh</Button>
                    <Button variant="light" onClick={clear}>Clear</Button>
                </Group>
            </Collapse>
        </Box>
    );
};

export default WorkflowComponentsSearch;


